import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";

const IMG = "https://images.unsplash.com/photo-1787539386490-4eeca293c066?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800&auto=format&fit=crop";

const sections = [
  {
    num: "01",
    title: "Our Vision",
    body: "ADD VALUE HOLDINGS LLC exists to build — brands, products, media, and ventures that stand on their own and grow more valuable over time. The ambition is long-term: a diversified company in which every division is excellent, and everything that carries the name has earned it.",
  },
  {
    num: "02",
    title: "Ownership",
    body: "ADD VALUE is owner-led and independent by design. Decisions are made with conviction rather than by committee, and the company answers to its own standard before anyone else's. Ownership means accountability — the name goes on nothing we would not stand behind.",
  },
  {
    num: "03",
    title: "Creativity & Development",
    body: "Creativity and business development are the same discipline here. A brand is an idea made durable; a product is a point of view made useful. ADD VALUE develops both — from the luxury apparel of ADD VALUE Clothing to the media and ventures still taking shape.",
  },
  {
    num: "04",
    title: "Building for the Future",
    body: "The company is built for long-term growth, not quick wins. Every division starts small and is built to last, and the holding structure is designed to expand — new brands, products, partnerships, and opportunities will be added as they are earned.",
  },
];

const phases = [
  { num: "Phase 01", title: "Brand Foundation", desc: "Establishing the identity, philosophy, and structure of ADD VALUE HOLDINGS LLC." },
  { num: "Phase 02", title: "Luxury Apparel", desc: "Developing ADD VALUE Clothing — the company's first consumer brand." },
  { num: "Phase 03", title: "Media & Content", desc: "Producing video, digital projects, and original content under the ADD VALUE name." },
  { num: "Phase 04", title: "New Ventures", desc: "Expanding into new businesses, products, and partnerships as opportunities emerge." },
];

const About = () => {
  useEffect(() => {
    document.title = "About — ADD VALUE HOLDINGS LLC";
  }, []);

  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        image={IMG}
        overline="About ADD VALUE"
        lines={["A company built on", "one simple philosophy."]}
        lede="ADD VALUE HOLDINGS LLC is an owner-led, diversified company focused on developing businesses, brands, products, and opportunities across multiple industries."
      />

      {/* QUOTE */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <blockquote
              data-testid="about-quote"
              className="max-w-4xl font-serif text-2xl leading-snug tracking-tight text-white/95 sm:text-3xl lg:text-4xl"
            >
              “Value is created through{" "}
              <span className="italic text-[#D4AF37]">vision</span>, action,
              relationships, and consistency.”
            </blockquote>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              ADD VALUE is not a finished story — it is a foundation. The
              company is deliberately structured as a holding company so that
              each venture, from apparel to media, stands on its own while
              sharing the same standard: if it carries the ADD VALUE name, it
              must add value — through its products, its brands, its
              relationships, and the opportunities it creates.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-t border-[#1c1c1f] bg-[#0d0d0f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-16">
            <Overline>What Drives Us</Overline>
          </Reveal>
          <div>
            {sections.map((s, i) => (
              <Reveal key={s.num} delay={0.05 * i}>
                <div
                  data-testid={`about-section-${s.num}`}
                  className="grid gap-6 border-t border-[#222226] py-12 lg:grid-cols-12 lg:gap-12 lg:py-16"
                >
                  <div className="lg:col-span-4">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]">
                      {s.num}
                    </p>
                    <h2 className="mt-4 font-serif text-2xl tracking-tight text-white sm:text-3xl">
                      {s.title}
                    </h2>
                  </div>
                  <p className="max-w-2xl text-sm font-light leading-relaxed text-stone-400 sm:text-base lg:col-span-8 lg:pt-8">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14">
            <Overline>The Road Ahead</Overline>
            <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Built in phases, built to last.
            </h2>
          </Reveal>
          <div className="grid gap-px border border-[#222226] bg-[#222226] sm:grid-cols-2 lg:grid-cols-4">
            {phases.map((p, i) => (
              <Reveal key={p.num} delay={0.08 * i} className="bg-[#0A0A0B]">
                <div
                  data-testid={`phase-${i + 1}`}
                  className="h-full p-8 transition-colors duration-500 hover:bg-[#121214]"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                    {p.num}
                  </p>
                  <h3 className="mt-5 font-serif text-xl text-white">{p.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-stone-500">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              data-testid="about-contact-link"
              to="/contact"
              className="group mt-14 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]"
            >
              Get in touch
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
